import { Box, BoxProps, Text, useColorModeValue } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import { useClient } from '../../core/client/ClientProvider'
import { useLanguage } from '../../hooks/utility/useLanguage'

interface AnswerLoaderProps extends BoxProps {
  knowledgeSource?: string
}

function resolveThinkingMessages(
  thinkingMessages: Record<string, string[]> | undefined,
  language: string,
  fallback: string,
): string[] {
  const messages = thinkingMessages?.[language] ?? thinkingMessages?.['en']
  return messages && messages.length > 0 ? messages : [fallback]
}

export const AnswerLoader: React.FC<AnswerLoaderProps> = ({ knowledgeSource, ...props }) => {
  const { config } = useClient()
  const { t, currentLanguage } = useLanguage()
  const messages = resolveThinkingMessages(
    config?.theme.customTexts.thinkingMessages,
    currentLanguage,
    t('chat.thinking'),
  )
  const [messageIndex, setMessageIndex] = useState(0)

  useEffect(() => {
    if (messages.length <= 1) return
    const interval = setInterval(() => {
      setMessageIndex(previous => (previous + 1) % messages.length)
    }, 6000)
    return () => clearInterval(interval)
  }, [messages.length])

  const shimmerGradient = useColorModeValue(
    'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,0) 100%)',
    'linear-gradient(90deg, rgba(26,32,44,0) 0%, rgba(26,32,44,0.2) 50%, rgba(26,32,44,0) 100%)',
  )

  return (
    <Box
      {...props}
      position='relative'
      _after={{
        content: '""',
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        background: shimmerGradient,
        animation: 'shimmer 2s infinite linear',
        zIndex: 1,
      }}
      sx={{
        '@keyframes shimmer': {
          '0%': { transform: 'translateX(-100%)' },
          '100%': { transform: 'translateX(100%)' },
        },
      }}
    >
      <Text fontSize='md' color={useColorModeValue('gray.700', 'gray.200')}>
        {messages[messageIndex % messages.length]}
      </Text>
    </Box>
  )
}
